import React, { useState, useEffect, RefObject } from "react";
import * as styles from "./StickyCruiseMenu.module.scss";
import { getDimensions, scrollTo } from "./utils";
import { SectionContainer } from "@silversea-ssc/ui-components-react";
import classNames from "classnames/bind";
import { Button } from "@silversea-ssc/ui-components-react";
import CruiseSummary, { CruiseSummaryProps } from "./CruiseSummary";

const cx = classNames.bind(styles);

export interface StickyCruiseMenuProps {
  menuRef?: React.RefObject<HTMLDivElement>;
  sectionRefs: {
    section: string;
    ref: RefObject<HTMLDivElement>;
    trackingName?: string;
  }[];
  summary: CruiseSummaryProps;
}

const StickyCruiseMenu: React.FC<StickyCruiseMenuProps> = ({
  menuRef,
  summary,
  sectionRefs
}) => {
  const [visibleSection, setVisibleSection] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      const { height: headerHeight } = menuRef?.current
        ? getDimensions(menuRef?.current)
        : { height: 0 };
      const scrollPosition = window.scrollY + headerHeight;
      const selected = sectionRefs.find(({ ref }) => {
        if (ref && ref.current) {
          const { offsetBottom, offsetTop } = getDimensions(ref.current);
          return scrollPosition > offsetTop && scrollPosition < offsetBottom;
        }
      });

      if (selected && selected.section !== visibleSection) {
        setVisibleSection(selected.section);
      } else if (!selected && visibleSection) {
        setVisibleSection(null);
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [sectionRefs, menuRef, visibleSection]);

  return (
    <div
      ref={menuRef}
      className={cx({
        menu: true,
        menuSticky: visibleSection != null
      })}
    >
      <SectionContainer className={styles.summary} backgroundColor="white">
        <CruiseSummary {...summary} />
      </SectionContainer>
      <SectionContainer
        className={styles.menuContainer}
        backgroundColor="transparent"
      >
        <nav className={styles.nav} data-content-name="sticky_cruise_menu">
          <React.Fragment>
            {sectionRefs.map((section, index) => (
              <div
                key={index}
                className={cx({
                  navItem: true,
                  navItemSelected: section.section == visibleSection
                })}
              >
                <Button
                  full
                  centerText
                  variant="link"
                  renderAs="button"
                  onClick={() => {
                    if (section.ref?.current && menuRef?.current)
                      scrollTo(section.ref.current, menuRef.current);
                  }}
                  data-content-piece={section.trackingName}
                >
                  {section.section}
                </Button>
              </div>
            ))}
          </React.Fragment>
        </nav>
      </SectionContainer>
    </div>
  );
};

// eslint-disable-next-line react/display-name
export default StickyCruiseMenu;
